<template>
  <div class="about-view" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
    <div class="about-container">
      
      <!-- Header -->
      <div class="header-section">
        <h1 class="main-title">About Me</h1>
        <p class="main-subtitle">
          MSc CS @ TalTech • AI/ML/NLP • researching local prompt optimization for LLMs
        </p>
      </div>

      <!-- Main Content Grid -->
      <div class="content-grid">
        
        <!-- Left Column -->
        <div class="left-column">
          
          <!-- Currently -->
          <div class="content-card">
            <div class="card-header">
              <span class="card-icon">🎓</span>
              <h3>Currently</h3>
            </div>
            <div class="card-body">
              <p>
                MSc in Computer Science at TalTech focusing on <strong>AI/ML/NLP</strong>.<br>
                Starting my MSc thesis on <strong>local prompt optimization</strong> for LLMs — privacy-friendly,
                cost-aware inference and small-model alignment.
              </p>
            </div>
          </div>

          <!-- Philosophy -->
          <div class="content-card">
            <div class="card-header">
              <span class="card-icon">⚙️</span>
              <h3>Philosophy</h3>
            </div>
            <div class="card-body">
              <p>
                Build things that make sense — simple, efficient, durable. Good engineering should make complexity
                feel obvious in hindsight.
              </p>
              <blockquote class="quote">“Simplicity is the ultimate sophistication.” — Leonardo da Vinci</blockquote>
              <blockquote class="quote">“Make it work, make it right, make it fast.” — Kent Beck</blockquote>
              <blockquote class="quote">“You may only build stuff that does not hurt others.” — V. Vojdani</blockquote>
            </div>
          </div>

        </div>

        <!-- Right Column -->
        <div class="right-column">
          
          <!-- My Approach -->
          <div class="content-card">
            <div class="card-header">
              <span class="card-icon">⚡</span>
              <h3>My Approach</h3>
            </div>
            <div class="card-body">
              <p>
                I choose <strong>unconventional stacks</strong> when they deliver clear upside. I optimize for
                <strong>latency, cost, and reliability</strong>, then iterate fast with tight feedback loops.
              </p>
            </div>
          </div>

          <!-- Beyond Code -->
          <div class="content-card">
            <div class="card-header">
              <span class="card-icon">🎮</span>
              <h3>Beyond Code</h3>
            </div>
            <div class="card-body">
              <p>
                I keep my brain sharp with <strong>chess</strong> and unwind with <strong>disc golf</strong>.
                I’m actively into <strong>investing</strong> — lately exploring <strong>arbitrage</strong> strategies.
                And yes, I also enjoy <strong>video games</strong> — stereotype accepted.
              </p>
            </div>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AboutView",
  data() {
    return {
      startY: 0,
      mobileBreakpoint: 768
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < this.mobileBreakpoint;
    }
  },
  methods: {
    handleTouchStart(event) {
      this.startY = event.touches[0].clientY;
    },
    handleTouchMove(event) {
      const touchY = event.touches[0].clientY;
      const deltaY = touchY - this.startY;
      const scrollTop = this.$el.scrollTop;
      const scrollHeight = this.$el.scrollHeight;
      const offsetHeight = this.$el.offsetHeight;
      if ((scrollTop === 0 && deltaY > 0) ||
          (scrollTop + offsetHeight >= scrollHeight && deltaY < 0)) {
        event.preventDefault();
      }
    }
  },
  mounted() {
    window.addEventListener("resize", () => {
      this.$forceUpdate();
    });
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.about-view {
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center; /* Center everything vertically */
  padding: 20px;
  color: var(--text-color);
}

.about-container {
  max-width: 1000px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Header */
.header-section {
  text-align: center;
  margin-bottom: 30px;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--text-color) 0%, #4CAF50 50%, #2196F3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.main-subtitle {
  font-size: 1.1rem;
  line-height: 1.5;
  opacity: 0.8;
  max-width: 680px;
  margin: 0 auto;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: center; /* Center grid vertically */
  justify-content: center;
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Cards */
.content-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 18px 20px;
  height: 260px; /* consistent height for nice grid alignment */
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.content-card:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* Header */
.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

/* Body vertically centered */
.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center; /* centers vertically */
  align-items: center;
  margin-top: 12px;
}

.card-icon {
  font-size: 1.3rem;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}

.content-card p {
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 0;
  max-width: 520px;
}

.content-card strong {
  color: #4CAF50;
  font-weight: 600;
}

/* Quotes */
.quote {
  opacity: 0.85;
  margin-top: 8px;
  font-size: 0.9rem;
  font-style: italic;
  max-width: 580px;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .about-view {
    padding: 16px;
  }

  .header-section {
    margin-bottom: 20px;
  }

  .main-title {
    font-size: 2rem;
  }

  .main-subtitle {
    font-size: 1rem;
  }

  .content-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .content-card {
    height: auto;
    padding: 16px;
  }

  .content-card p, .quote {
    font-size: 0.9rem;
  }
}
</style>